import { createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { all } from "redux-saga/effects";

import { deploymentsActions } from "@ds/modules/deployments/redux/slice";
import { iotEventUpdate } from "@ds/modules/iot/redux/actions";
import { IoTTopicPathEnum } from "@ds/modules/iot/utils/model";
import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";
import { playlistAssetsActions } from "@ds/modules/content/playlist-assets/redux/slice";
import { isApiPlaylistAssetQueryOutput } from "@ds/modules/content/playlist-assets/utils/model";

import { type ProgressInfo } from "@ds/services/storage";
import { getIds } from "@ds/utils/entities";
import { LoadStatus, type BaseMeta } from "@ds/utils/reducer";
import { type SagaOptions } from "@ds/utils/saga-helpers";

import { createAssetFiltersByTableType, getAssetFileNameFromProgressInfo } from "../utils/helpers";
import { isAsset } from "../utils/model";
import { assetsCommonActions } from "./common-slice";
import { watchAssets } from "./sagas/assets";
import { watchAssetThumbnails } from "./sagas/thumbnails";

export enum AssetsRemoteOperation {
  CRUD = "crud",
  CHECKBOXES = "checkboxes",
  THUMBNAILS = "thumbnails",
  IOT_FETCH = "iot_fetch",
}

export type AssetsProgressChannelEvent = {
  progressInfo: ProgressInfo;
};

export type AssetsMeta = BaseMeta & {
  remoteOperation: AssetsRemoteOperation;
  filters?: KeysToCamelCase<AssetFilters>;
};

export type AssetUploadsMap = {
  [fileName: string]: {
    loadStatus: LoadStatus;
    error: string | null;

    progressInfo: ProgressInfo | null;
  };
};

export type AssetThumbnailUploadsMap = {
  [fileName: string]: {
    loadStatus: LoadStatus;
    error: string | null;

    assetId: number | null;
    progressInfo: ProgressInfo | null;
  };
};

interface AssetsState {
  loadStatusMap: {
    [AssetsRemoteOperation.CRUD]: LoadStatus;
    [AssetsRemoteOperation.CHECKBOXES]: LoadStatus;
    [AssetsRemoteOperation.THUMBNAILS]: LoadStatus;
    [AssetsRemoteOperation.IOT_FETCH]: LoadStatus;
  };

  error: string | null;

  entities: {
    [assetId: number]: Asset;
  };

  ids: number[];

  tableIds: Record<string, number[]>;
  selectedIds: Record<string, number[]>;

  isUploadModalMinimized: boolean;
  uploadsByFilename: AssetUploadsMap;
  thumbnailUploadsByFilename: AssetThumbnailUploadsMap;
}
const entityAdapter = createEntityAdapter<Asset>();

export const initialState: AssetsState = entityAdapter.getInitialState({
  loadStatusMap: {
    [AssetsRemoteOperation.CRUD]: LoadStatus.Idle,
    [AssetsRemoteOperation.CHECKBOXES]: LoadStatus.Idle,
    [AssetsRemoteOperation.THUMBNAILS]: LoadStatus.Idle,
    [AssetsRemoteOperation.IOT_FETCH]: LoadStatus.Idle,
  },

  error: null,

  tableIds: {
    [MainTableDataTypeEnum.AssetVideos]: [],
    [MainTableDataTypeEnum.AssetImages]: [],
    [MainTableDataTypeEnum.AssetAudio]: [],
  },
  selectedIds: {
    [MainTableDataTypeEnum.AssetVideos]: [],
    [MainTableDataTypeEnum.AssetImages]: [],
    [MainTableDataTypeEnum.AssetAudio]: [],
  },

  isUploadModalMinimized: false,
  uploadsByFilename: {},
  thumbnailUploadsByFilename: {},
}) as AssetsState;

const getTableTypeOrDefault = (
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = MainTableDataTypeEnum.AssetVideos,
) => tableType;

const removeEntities = (
  state: AssetsState,
  payload: number[],
  tableType: MainTableDataTypeEnum.AssetVideos | MainTableDataTypeEnum.AssetImages | MainTableDataTypeEnum.AssetAudio,
) => {
  entityAdapter.removeMany(state, payload);

  state.tableIds[tableType] = state.tableIds[tableType].filter(id => !payload.includes(id));
  state.selectedIds[tableType] = state.selectedIds[tableType].filter(id => !payload.includes(id));
};

const sliceType = "ASSETS";
const slice = createSlice({
  name: sliceType,
  initialState,
  reducers: {
    fetchAssets: {
      reducer(state, { meta }: PayloadAction<undefined, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;

        if (meta.remoteOperation === AssetsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] =
            state.tableIds[getTableTypeOrDefault(meta.options?.tableType)];
        }
      },
      prepare(
        filters: KeysToCamelCase<AssetFilters> = {},
        options: SagaOptions = {},
        remoteOperation: AssetsRemoteOperation = AssetsRemoteOperation.CRUD,
      ) {
        return {
          payload: undefined,
          meta: {
            remoteOperation,
            filters,
            options,
          },
        };
      },
    },
    fetchAssetsFailed: {
      reducer(state, { meta, error }: PayloadAction<undefined, string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;

        if (meta.remoteOperation === AssetsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = [];
        }
      },
      prepare(meta: AssetsMeta, { message }: ErrorLike) {
        return { payload: undefined, meta, error: message };
      },
    },
    fetchAssetsSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<QueryOutput<Asset>, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setMany(state, payload.items);

        if (meta.remoteOperation === AssetsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
        } else {
          state.tableIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
            getTableTypeOrDefault(meta.options?.tableType)
          ].filter(id => state.ids.includes(id));
        }
      },
      prepare(payload: QueryOutput<Asset>, meta: AssetsMeta) {
        return { payload, meta };
      },
    },
    fetchAsset: {
      reducer(state, { meta }: PayloadAction<number, string, AssetsMeta, never>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: number, options: SagaOptions = {}) {
        return { payload, meta: { remoteOperation: AssetsRemoteOperation.CRUD, options } };
      },
    },
    fetchAssetFailed: {
      reducer(state, { meta, error }: PayloadAction<number, string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: number, meta: AssetsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    fetchAssetSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Asset, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, payload);

        state.tableIds[getTableTypeOrDefault(meta.options?.tableType)] = state.tableIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].filter(id => id !== payload.id);

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].filter(id => id !== payload.id);
      },
      prepare(payload: Asset, meta: AssetsMeta) {
        return { payload, meta };
      },
    },
    deleteAssets: {
      reducer(state, { meta }: PayloadAction<number[], string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(idsToDelete: number[], redirectTo?: string) {
        return {
          payload: idsToDelete,
          meta: {
            remoteOperation: AssetsRemoteOperation.CRUD,
            options: { redirectTo },
          } as AssetsMeta,
        };
      },
    },
    deleteAssetsFailed: {
      reducer(state, { meta, error }: PayloadAction<number[], string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(idsToDelete: number[], meta: AssetsMeta, { message }: ErrorLike) {
        return { payload: idsToDelete, meta, error: message };
      },
    },
    deleteAssetsSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        removeEntities(state, payload, MainTableDataTypeEnum.AssetVideos);
        removeEntities(state, payload, MainTableDataTypeEnum.AssetImages);
        removeEntities(state, payload, MainTableDataTypeEnum.AssetAudio);
      },
      prepare(idsToDelete: number[], meta: AssetsMeta) {
        return { payload: idsToDelete, meta };
      },
    },
    selectAssets: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload;
      },
      prepare(assetsOrIds: Asset[] | number[], tableType: MainTableDataTypeEnum) {
        return {
          payload: getIds(assetsOrIds),
          meta: {
            remoteOperation: AssetsRemoteOperation.CHECKBOXES,
            options: { tableType },
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    toggleSelected: {
      reducer(state, { payload, meta }: PayloadAction<number, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].includes(payload)
          ? state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)].filter(id => id !== payload)
          : [...state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)], payload];
      },
      prepare(payload: number, tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.CHECKBOXES,
            options: { tableType },
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    uploadAssets: {
      reducer(state, { payload, meta }: PayloadAction<File[], string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;

        const allFinished = Object.keys(state.uploadsByFilename).every(
          fileName => state.uploadsByFilename[fileName].loadStatus !== LoadStatus.Loading,
        );

        if (allFinished) {
          state.uploadsByFilename = {};
        }

        payload.forEach(({ name }) => {
          state.uploadsByFilename[name] = { loadStatus: LoadStatus.Loading, error: null, progressInfo: null };
        });
      },
      prepare(payload: File[], tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.CRUD,
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    uploadAsset: {
      reducer(state, { payload, meta }: PayloadAction<File, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        const allFinished = Object.keys(state.uploadsByFilename).every(
          fileName => state.uploadsByFilename[fileName].loadStatus !== LoadStatus.Loading,
        );

        if (allFinished) {
          state.uploadsByFilename = {};
        }

        state.uploadsByFilename[payload.name] = {
          loadStatus: LoadStatus.Loading,
          error: null,

          progressInfo: null,
        };
      },
      prepare(payload: File, tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.CRUD,
            filters: createAssetFiltersByTableType({ tableType, name: payload.name }),
          },
        };
      },
    },
    uploadAssetFailed: {
      reducer(state, { payload, meta, error }: PayloadAction<string, string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.uploadsByFilename[payload].loadStatus = LoadStatus.Failed;
        state.uploadsByFilename[payload].error = error;
      },
      prepare({ name }: File, meta: AssetsMeta, { message }: ErrorLike) {
        return { payload: name, meta, error: message };
      },
    },
    uploadAssetSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Asset, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.uploadsByFilename[payload.name].loadStatus = LoadStatus.Succeeded;
        state.uploadsByFilename[payload.name].error = null;

        entityAdapter.setOne(state, payload);
      },
      prepare(payload: Asset, meta: AssetsMeta) {
        return { payload, meta };
      },
    },
    progressUploadingAsset: (state, { payload }: PayloadAction<ProgressInfo>) => {
      const fileName = getAssetFileNameFromProgressInfo(payload);
      if (fileName) {
        state.uploadsByFilename[fileName].progressInfo = payload;
      }
    },
    deleteAssetThumbnail: {
      reducer(state, { meta }: PayloadAction<Thumbnail, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: Thumbnail, tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.THUMBNAILS,
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    deleteAssetThumbnailFailed: {
      reducer(state, { meta, error }: PayloadAction<Thumbnail, string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: Thumbnail, meta: AssetsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    deleteAssetThumbnailSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Thumbnail, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.updateOne(state, {
          id: payload.asset_id,
          changes: {
            thumbnail: undefined,
          },
        });
      },
      prepare(payload: Thumbnail, meta: AssetsMeta) {
        return { payload, meta };
      },
    },
    uploadAssetThumbnails: {
      reducer(state, { payload, meta }: PayloadAction<[{ thumbnailFile: File; asset: Asset }], string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        const allFinished = Object.keys(state.thumbnailUploadsByFilename).every(
          fileName => state.thumbnailUploadsByFilename[fileName].loadStatus !== LoadStatus.Loading,
        );

        if (allFinished) {
          state.thumbnailUploadsByFilename = {};
        }

        payload.forEach(({ thumbnailFile: { name } }) => {
          state.thumbnailUploadsByFilename[name] = {
            loadStatus: LoadStatus.Loading,
            error: null,

            assetId: null,
            progressInfo: null,
          };
        });
      },
      prepare(payload: [{ thumbnailFile: File; asset: Asset }], tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.THUMBNAILS,
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    uploadAssetThumbnail: {
      reducer(
        state,
        { payload, meta }: PayloadAction<{ thumbnailFile: File; asset: Asset }, string, AssetsMeta, never>,
      ) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.thumbnailUploadsByFilename[payload.thumbnailFile.name] = {
          loadStatus: LoadStatus.Loading,
          error: null,

          assetId: null,
          progressInfo: null,
        };
      },
      prepare(payload: { thumbnailFile: File; asset: Asset }, tableType: MainTableDataTypeEnum) {
        return {
          payload,
          meta: {
            remoteOperation: AssetsRemoteOperation.THUMBNAILS,
            filters: createAssetFiltersByTableType({ tableType }),
          },
        };
      },
    },
    uploadAssetThumbnailFailed: {
      reducer(state, { payload, meta, error }: PayloadAction<string, string, AssetsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.thumbnailUploadsByFilename[payload].loadStatus = LoadStatus.Failed;
        state.thumbnailUploadsByFilename[payload].error = error;
      },
      prepare(payload: { thumbnailFile: File; asset: Asset }, meta: AssetsMeta, { message }: ErrorLike) {
        return { payload: payload.thumbnailFile.name, meta, error: message };
      },
    },
    uploadAssetThumbnailSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Thumbnail, string, AssetsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.thumbnailUploadsByFilename[payload.file_name].loadStatus = LoadStatus.Succeeded;
        state.thumbnailUploadsByFilename[payload.file_name].error = null;

        entityAdapter.updateOne(state, {
          id: payload.asset_id,
          changes: {
            thumbnail: payload,
          },
        });
      },
      prepare(payload: Thumbnail, meta: AssetsMeta) {
        return { payload, meta };
      },
    },
    progressUploadingAssetThumbnail: (state, { payload }: PayloadAction<ProgressInfo>) => {
      const fileName = getAssetFileNameFromProgressInfo(payload);
      if (fileName) {
        state.thumbnailUploadsByFilename[fileName].progressInfo = payload;
      }
    },
    setUploadModalMinimized: (state, { payload }: PayloadAction<boolean>) => {
      state.isUploadModalMinimized = payload;
    },
    iotUpdate: (state, { payload }: PayloadAction<Asset>) => {
      if (payload.audit?.deleted_at) {
        removeEntities(state, [payload.id], MainTableDataTypeEnum.AssetVideos);
        removeEntities(state, [payload.id], MainTableDataTypeEnum.AssetImages);
        removeEntities(state, [payload.id], MainTableDataTypeEnum.AssetAudio);
      } else {
        entityAdapter.setOne(state, payload);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(assetsCommonActions.switchView, state => {
      state.loadStatusMap[AssetsRemoteOperation.CRUD] = LoadStatus.Idle;
      state.error = null;
    });

    builder.addCase(playlistAssetsActions.fetchPlaylistAssetsSucceeded, (state, { payload }) => {
      if (isApiPlaylistAssetQueryOutput(payload)) {
        entityAdapter.setMany(
          state,
          payload.items.map(({ asset }) => asset),
        );
      }
    });

    builder.addCase(playlistAssetsActions.createPlaylistAssetSucceeded, (state, { payload }) => {
      entityAdapter.setOne(state, payload.asset);
    });

    builder.addCase(playlistAssetsActions.updatePlaylistAssetsSucceeded, (state, { payload }) => {
      entityAdapter.setMany(state, payload.flatMap(({ asset }) => asset || []).filter(isAsset));
    });

    builder.addCase(playlistAssetsActions.uploadPlaylistAssetSucceeded, (state, { payload }) => {
      entityAdapter.setOne(state, payload.asset);
    });

    builder.addCase(deploymentsActions.fetchDeploymentsSucceeded, (state, { payload, meta }) => {
      if (!meta.options?.cache?.fetchedFromCache) {
        entityAdapter.setMany(state, payload.items.flatMap(({ details }) => details?.entities || []).filter(isAsset));
      }
    });

    builder.addCase(deploymentsActions.fetchDeploymentSucceeded, (state, { payload, meta }) => {
      if (!meta.options?.cache?.fetchedFromCache) {
        entityAdapter.setMany(state, (payload.details?.entities || []).filter(isAsset));
      }
    });

    builder.addCase(deploymentsActions.createDeploymentSucceeded, (state, { payload }) => {
      entityAdapter.setMany(state, (payload.details?.entities || []).filter(isAsset));
    });

    builder.addCase(deploymentsActions.updateDeploymentsSucceeded, (state, { payload }) => {
      entityAdapter.setMany(state, payload.flatMap(({ details }) => details?.entities || []).filter(isAsset));
    });

    builder.addCase(iotEventUpdate(IoTTopicPathEnum.PlaylistAssets), (state, { payload }) => {
      entityAdapter.setOne(state, payload.asset);
    });

    builder.addCase(iotEventUpdate(IoTTopicPathEnum.ContentThumbnails), (state, { payload }) => {
      if (state.entities[payload.asset_id]) {
        state.entities[payload.asset_id].thumbnail = payload.audit?.deleted_at ? undefined : payload;
      }
    });
  },
});

export const { name: assetsType, actions: assetsActions, reducer: assetsReducer } = slice;

export const entitySelectors = entityAdapter.getSelectors();

export function* rootAssetsSaga() {
  yield all([watchAssets(), watchAssetThumbnails()]);
}
